import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Snowflake } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PRIMARY_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/demo", label: "Demo" },
];

const SERVICE_LINKS = [
  { to: "/services/ac-repair", label: "AC Repair" },
  { to: "/services/maintenance", label: "Maintenance" },
  { to: "/services/replacement", label: "Replacement" },
  { to: "/services/indoor-air-quality", label: "Indoor Air Quality" },
];

function Wordmark() {
  return (
    <Link
      to="/"
      data-testid="nav-logo"
      className="group inline-flex items-center gap-2"
      aria-label="Sun Chaser Cooling — go to homepage"
    >
      <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-navy text-white shadow-soft">
        <Snowflake className="h-4 w-4 text-aqua" />
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-sun shadow-sunGlow" />
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-display text-lg font-bold tracking-tight text-navy">
          Sun Chaser
        </span>
        <span className="font-sans text-[11px] font-medium uppercase tracking-[0.16em] text-navy/55">
          Cooling
        </span>
      </span>
    </Link>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-mist-line bg-white/85 backdrop-blur-md shadow-soft"
          : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="container-page flex h-16 items-center justify-between gap-6 md:h-[72px]">
        <Wordmark />

        <nav
          aria-label="Primary"
          className="hidden items-center gap-1 lg:flex"
        >
          {PRIMARY_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-navy"
                    : "text-navy/70 hover:text-navy"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {link.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-aqua" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
          <div className="mx-2 h-5 w-px bg-mist-line" aria-hidden="true" />
          {SERVICE_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-navy"
                    : "text-navy/65 hover:text-navy"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/request-service"
            data-testid="nav-cta-request"
            className="hidden items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white shadow-soft transition-all hover:bg-navy-soft hover:shadow-lift sm:inline-flex"
          >
            Request Service
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-aqua" aria-hidden="true" />
          </Link>
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-mist-line bg-white text-navy transition-colors hover:bg-mist lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-drawer"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            data-testid="nav-mobile-drawer"
            className="border-t border-mist-line bg-white lg:hidden"
          >
            <div className="container-page flex flex-col gap-1 py-4">
              {[...PRIMARY_LINKS, ...SERVICE_LINKS].map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-xl px-3 py-3 text-base font-medium ${
                      isActive
                        ? "bg-mist text-navy"
                        : "text-navy/80 hover:bg-mist"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/request-service"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-request"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-4 py-3 text-base font-semibold text-white shadow-soft hover:bg-navy-soft"
              >
                Request Service
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
